<template>
  <keep-alive>
    <v-container fluid>
      <!-- Confirmation Dialog for Unscheduling -->
      <v-dialog v-model="showUnscheduleConfirm" max-width="400px">
        <v-card>
          <v-card-title class="text-h6">Confirm Unschedule</v-card-title>
          <v-card-text>
            Are you sure you want to unschedule this meal?
            <div class="mt-2 font-weight-medium">{{ mealToUnschedule ? mealToUnschedule.name : '' }}</div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showUnscheduleConfirm = false">Cancel</v-btn>
            <v-btn
              color="red"
              text
              @click="confirmUnschedule"
              :loading="unscheduling"
              :disabled="readOnly"
            >
              Unschedule
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Insert Day Dialog -->
      <v-dialog v-model="showInsertDayDialog" max-width="400px">
        <v-card>
          <v-card-title class="text-h6">Insert Day</v-card-title>
          <v-card-text>
            <div class="mb-3">
              Insert a day into the schedule for a specific diet. This will shift all meals for that diet on or after
              the selected date forward by one day.
            </div>

            <v-text-field
              v-model="insertDayDate"
              label="Date"
              type="date"
              class="mb-3"
            ></v-text-field>

            <v-select
              v-model="insertDayDiet"
              :items="dietSelectItems.filter(d => d.id !== null)"
              item-text="name"
              item-value="id"
              label="Diet"
              class="mb-3"
            ></v-select>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showInsertDayDialog = false">Cancel</v-btn>
            <v-btn
              color="primary"
              text
              @click="confirmInsertDay"
              :disabled="!insertDayDate || !insertDayDiet || readOnly"
              :loading="insertingDay"
            >
              Insert Day
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Remove Day Dialog -->
      <v-dialog v-model="showRemoveDayDialog" max-width="400px">
        <v-card>
          <v-card-title class="text-h6">Remove Day</v-card-title>
          <v-card-text>
            <div class="mb-3">
              Remove a day from the schedule for a specific diet. This will shift all meals for that diet after the
              selected date backward by one day.
              <strong>The selected date must have no meals scheduled.</strong>
            </div>

            <v-text-field
              v-model="removeDayDate"
              label="Date"
              type="date"
              class="mb-3"
              @change="checkRemoveDayValid"
            ></v-text-field>

            <v-select
              v-model="removeDayDiet"
              :items="dietSelectItems.filter(d => d.id !== null)"
              item-text="name"
              item-value="id"
              label="Diet"
              class="mb-3"
              @change="checkRemoveDayValid"
            ></v-select>

            <v-alert
              v-if="removeDayError"
              type="error"
              dense
              class="mb-3"
            >
              {{ removeDayError }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showRemoveDayDialog = false">Cancel</v-btn>
            <v-btn
              color="red"
              text
              @click="confirmRemoveDay"
              :disabled="!removeDayDate || !removeDayDiet || !!removeDayError || readOnly"
              :loading="removingDay"
            >
              Remove Day
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Swap Meals Dialog -->
      <v-dialog v-model="showSwapDialog" max-width="600px">
        <v-card>
          <v-card-title class="text-h6">Swap Meals</v-card-title>
          <v-card-text>
            <div class="mb-3">
              <strong>Source Meal:</strong> {{ mealToSwap ? mealToSwap.name : '' }}
              <div v-if="mealToSwap" class="mt-2 ml-3">
                <v-chip small :color="getTimeOfDayColor(mealToSwap.tod)" dark class="mr-2">
                  {{ dateFormatted(mealToSwap.date, {formatString: 'ddd MMM DD'}) }} ({{
                    getTimeOfDay(mealToSwap.tod)
                  }})
                </v-chip>
                <v-chip small :color="getDietColor(mealToSwap.diet)" text-color="white">
                  {{ getDietName(mealToSwap.diet) }}
                </v-chip>
              </div>
            </div>

            <v-select
              v-model="selectedSwapTod"
              :items="todOptions"
              item-text="name"
              item-value="value"
              label="Filter by Time of Day"
              class="mb-3"
              clearable
            ></v-select>

            <v-autocomplete
              :items="availableMealsForSwap"
              :item-text="meal => `${meal.name} - ${dateFormatted(meal.date, {formatString: 'MMM DD'})} (${getTimeOfDay(meal.tod)})`"
              item-value="id"
              return-object
              label="Select meal to swap with"
              v-model="selectedSwapTargetMeal"
              clearable
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip x-small :color="getDietColor(item.diet)" text-color="white">
                      {{ getDietName(item.diet) }}
                    </v-chip>
                    {{ dateFormatted(item.date, {formatString: 'MMM DD'}) }} ({{ getTimeOfDay(item.tod) }})


                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <v-alert
              v-if="selectedSwapTargetMeal"
              type="info"
              dense
              class="mt-3"
            >
              <div class="subtitle-2 mb-2">This will swap:</div>
              <div class="body-2">
                <strong>{{ mealToSwap ? mealToSwap.name : '' }}</strong><br/>
                From: {{ mealToSwap ? dateFormatted(mealToSwap.date, {formatString: 'ddd MMM DD'}) : '' }}
                ({{ mealToSwap ? getTimeOfDay(mealToSwap.tod) : '' }})<br/>
                To: {{
                  selectedSwapTargetMeal ? dateFormatted(selectedSwapTargetMeal.date, {formatString: 'ddd MMM DD'}) : ''
                }} ({{ selectedSwapTargetMeal ? getTimeOfDay(selectedSwapTargetMeal.tod) : '' }})
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="body-2">
                <strong>{{ selectedSwapTargetMeal ? selectedSwapTargetMeal.name : '' }}</strong><br/>
                From: {{
                  selectedSwapTargetMeal ? dateFormatted(selectedSwapTargetMeal.date, {formatString: 'ddd MMM DD'}) : ''
                }} ({{ selectedSwapTargetMeal ? getTimeOfDay(selectedSwapTargetMeal.tod) : '' }})<br/>
                To: {{ mealToSwap ? dateFormatted(mealToSwap.date, {formatString: 'ddd MMM DD'}) : '' }}
                ({{ mealToSwap ? getTimeOfDay(mealToSwap.tod) : '' }})
              </div>
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showSwapDialog = false">Cancel</v-btn>
            <v-btn
              color="primary"
              text
              @click="confirmSwap"
              :disabled="!selectedSwapTargetMeal || readOnly"
              :loading="swapping"
            >
              Swap Meals
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Schedule Meal Dialog -->
      <v-dialog v-model="showScheduleMealDialog" max-width="600px">
        <v-card>
          <v-card-title class="text-h6">Schedule Meal</v-card-title>
          <v-card-text>
            <div class="mb-3">
              <strong>Slot:</strong> {{
                schedulingSlot ? `${schedulingSlot.dietName} - ${schedulingSlot.tod} on ${dateFormatted(schedulingSlot.date)}` : ''
              }}
            </div>

            <v-select
              v-model="selectedTod"
              :items="todOptions"
              item-text="name"
              item-value="value"
              label="Time of Day"
              class="mb-3"
              @change="fetchMealsForTod"
            ></v-select>

            <v-text-field
              v-model.number="maxDaysOld"
              type="number"
              label="Max days old"
              hint="Only show meals from the last X days"
              class="mb-3"
              @input="fetchMealsForTod"
            ></v-text-field>

            <v-autocomplete
              :items="unscheduledMeals"
              :item-text="sku => sku.name"
              item-value="id"
              return-object
              label="Select meal SKU to schedule"
              v-model="selectedMealToSchedule"
              clearable
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="d-flex align-center">
                    {{ item.id }}
                    <v-chip
                      v-if="isSkuAlreadyUsed(item.id)"
                      x-small
                      color="warning"
                      class="ml-2"
                    >
                      Already Used
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.name }} - Most recently used {{ formatDate(item.latest_meal_date) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <v-alert
              v-if="isSelectedSkuAlreadyUsed"
              type="warning"
              dense
              class="mt-2"
            >
              This SKU is already used in the current schedule.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showScheduleMealDialog = false">Cancel</v-btn>
            <v-btn
              color="primary"
              text
              @click="confirmScheduleMeal"
              :disabled="!selectedMealToSchedule || readOnly"
              :loading="scheduling"
            >
              Schedule
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-toolbar flat>
        <div class="d-flex align-center">
          <v-text-field
            v-model="start"
            label="Start Date"
            type="date"
            dense
            style="max-width: 160px"
            class="mr-3"
          />
          <v-text-field
            v-model="daysToShow"
            label="Days"
            type="number"
            class="mr-3"
            dense
            style="max-width: 80px"
            min="1"
            max="365"
          />
        </div>
        <v-select
          v-if="!loading"
          v-model="selectedDiet"
          :items="dietSelectItems"
          item-text="name"
          item-value="id"
          label="Diet"
          dense
          style="max-width: 200px;"
        />
        <v-spacer/>

        <v-checkbox
          v-model="enableSimilarityComparisons"
          label="Similarity"
          hint="Compare Meals"
          dense
          class="mr-4 mt-0 pt-0"
          hide-details
        ></v-checkbox>

        <v-btn
          small
          color="green"
          dark
          @click="openInsertDayDialog"
          :disabled="readOnly"
          class="mr-3"
        >
          <v-icon small left>mdi-calendar-plus</v-icon>
          Insert Day
        </v-btn>

        <v-btn
          small
          color="red"
          dark
          @click="openRemoveDayDialog"
          :disabled="readOnly"
          class="mr-3"
        >
          <v-icon small left>mdi-calendar-minus</v-icon>
          Remove Day
        </v-btn>

      </v-toolbar>


      <template v-if="!loading">
        <v-row>
          <v-col>
            <!-- Meal Count Display -->
            <div class="text-center mb-3">
              <v-chip
                color="primary"
                text-color="white"
                class="px-4"
              >
                <v-icon small left>mdi-silverware-fork-knife</v-icon>
                {{ totalDisplayedMeals }} meal{{ totalDisplayedMeals !== 1 ? 's' : '' }} displayed
              </v-chip>
            </div>

            <!-- Show Earlier Button and Filter Buttons -->
            <div class="d-flex align-center justify-center mb-4 px-4">
              <v-btn
                large
                color="primary"
                @click="showEarlier"
                class="mr-4"
              >
                <v-icon left>mdi-chevron-left</v-icon>
                Earlier
              </v-btn>
              <!-- Show Later Button -->
              <div class="text-center ">
                <v-btn
                  large
                  color="primary"
                  @click="showLater"
                >
                  Later
                  <v-icon left>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
              <v-spacer/>
              <v-btn
                small
                :color="showQualityIssuesOnly ? 'red' : ''"
                :dark="showQualityIssuesOnly"
                @click="toggleQualityIssueFilter"
                class="mr-3"
              >
                <v-icon small left>mdi-alert-circle</v-icon>
                Quality Issues ({{ qualityIssueCount }})
              </v-btn>

              <v-btn
                small
                :color="showMissingMealsOnly ? 'orange' : ''"
                :dark="showMissingMealsOnly"
                @click="toggleMissingMealsFilter"
                class="mr-3"
              >
                <v-icon small left>mdi-calendar-alert</v-icon>
                Missing Meals ({{ missingMealsCount }})
              </v-btn>

              <v-btn
                v-if="duplicateSkuCount > 0"
                small
                :color="showDuplicateSkusOnly ? 'purple' : ''"
                :dark="showDuplicateSkusOnly"
                @click="toggleDuplicateSkusFilter"
                class="mr-3"
              >
                <v-icon small left>mdi-content-duplicate</v-icon>
                Duplicate SKUs ({{ duplicateSkuCount }})
              </v-btn>

              <v-btn
                v-if="enableSimilarityComparisons && highSimilarityCount > 0"
                small
                :color="showHighSimilarityOnly ? 'deep-orange' : ''"
                :dark="showHighSimilarityOnly"
                @click="toggleHighSimilarityFilter"
                class="mr-3"
              >
                <v-icon small left>mdi-chart-scatter-plot</v-icon>
                Similar Meals ({{ highSimilarityCount }}) ≥{{ Math.round(dynamicSimilarityThreshold * 100) }}%
              </v-btn>
            </div>

            <!-- Duplicate SKU selector toolbar (shown when duplicate filter is active) -->
            <v-toolbar v-if="!loading && showDuplicateSkusOnly" flat dense class="mb-3">
              <div class="subtitle-2 mr-3">Filter by duplicate SKU:</div>
              <v-btn
                small
                :color="selectedDuplicateSku === null ? 'primary' : ''"
                :dark="selectedDuplicateSku === null"
                @click="selectedDuplicateSku = null"
                class="mr-2"
              >
                All Duplicates
              </v-btn>
              <v-btn
                v-for="duplicateSku in duplicateSkusCache"
                :key="duplicateSku.sku"
                small
                :color="selectedDuplicateSku === duplicateSku.sku ? 'purple' : ''"
                :dark="selectedDuplicateSku === duplicateSku.sku"
                @click="selectedDuplicateSku = duplicateSku.sku"
                class="mr-2"
              >
                {{ duplicateSku.sku }} ({{ duplicateSku.count }})
              </v-btn>
            </v-toolbar>

            <v-timeline
              dense
              align-top
            >
              <v-timeline-item
                v-for="(dayMeals, date) in mealsByDate"
                :key="date"
                :color="getDateColor(date)"
                large
              >
                <template v-slot:icon>
                  <v-icon>{{ getDateIcon(date) }}</v-icon>
                </template>

                <v-card class="">

                  <v-card-title class="text-h6">
                    <div class="headline font-weight-light">
                      {{ dateFormatted(date, {formatString: 'dddd DD MMM YYYY'}) }}
                    </div>
                    <v-spacer/>
                    {{ dayMeals.length }} meal{{ dayMeals.length !== 1 ? 's' : '' }} scheduled
                  </v-card-title>
                  <v-card-text>
                    <!-- Empty slots for missing meals -->
                    <v-row v-for="emptySlot in getEmptySlotsForDate(date)"
                           :key="`empty-${emptySlot.diet}-${emptySlot.tod}`" class="mb-4">
                      <template v-if="selectedDiet === null || selectedDiet === emptySlot.diet">
                        <v-col>
                          <v-card outlined class="empty-meal-slot" style="border: 2px dashed #ccc; min-height: 100px;">
                            <v-card-text class="text-center">
                              <div class="subtitle-2 mb-2">Missing {{ emptySlot.tod }}</div>
                              <v-chip
                                small
                                :color="getDietColor(emptySlot.diet)"
                                dark
                                class="mb-2"
                              >
                                {{ getDietName(emptySlot.diet) }}
                              </v-chip>
                              <br>
                              <v-btn
                                small
                                color="primary"
                                @click="openScheduleMealDialog(date, emptySlot.diet, emptySlot.tod)"
                                :disabled="readOnly"
                              >
                                <v-icon small left>mdi-plus</v-icon>
                                Schedule Meal
                              </v-btn>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </template>
                    </v-row>
                    <!-- Existing scheduled meals -->
                    <v-row v-for="meal in dayMeals" :key="meal.id" class="mb-4">
                      <v-col>
                        <v-card outlined :id="`meal-${meal.id}`">
                          <v-card-title class="py-1 pt-0">
                            <router-link
                              :to="{ name: 'MealDesign', params: { id: meal.id, date: formatDate(meal.date)}}">
                              {{ meal.name }}
                            </router-link>
                            <router-link :to="{ name: 'MealVersions', query: { sku: meal.sku } }"
                                         class="ml-2 caption">
                              ({{ meal.sku }})
                            </router-link>
                            <v-spacer/>

                            <v-btn
                              v-if="getNextInstanceId(meal)"
                              @click="jumpToNextInstance(meal)"
                              color="green"
                              class="ma-2"
                              dark
                              small
                            >
                              Go To Latest Instance
                            </v-btn>

                            <v-btn
                              color="blue"
                              dark
                              @click="openSwapDialog(meal)"
                              v-if="!(readOnly || getNextInstanceId(meal))"
                              title="Swap this meal with another"
                              class="ma-2"
                              small
                            >
                              <v-icon small>mdi-swap-horizontal</v-icon>
                              Swap
                            </v-btn>
                            <v-btn
                              color="grey"
                              dark
                              @click="unscheduleMeal(meal)"
                              v-if="!(readOnly || getNextInstanceId(meal))"
                              title="Unschedule this meal"
                              class="ma-2"
                              small
                            >
                              <v-icon small>mdi-calendar-remove</v-icon>
                              Unschedule
                            </v-btn>
                            <v-icon
                              v-if="getDuplicateSkus().some(d => d.sku === meal.sku)"
                              small
                              color="purple"
                              class="ml-1"
                              title="Duplicate SKU in date range"
                            >
                              mdi-content-duplicate
                            </v-icon>
                          </v-card-title>
                          <v-card-title class="py-1">
                            <v-chip
                              v-if="meal.tod && meal.date"
                              small
                              :color="getTimeOfDayColor(meal.tod)"
                              dark
                              class="mr-2"
                            >
                              {{ dateFormatted(meal.date, {formatString: 'ddd MMM DD'}) }} ({{
                                getTimeOfDay(meal.tod)
                              }})
                            </v-chip>
                            <v-spacer/>
                            <v-chip
                              v-if="meal.diet"
                              small
                              :color="getDietColor(meal.diet)"
                              text-color="white"
                            >
                              {{ getDietName(meal.diet) }}
                            </v-chip>
                          </v-card-title>

                          <v-card-text v-if="ratings[meal.id] || feedback[meal.id]" class="pt-0">
                            <v-row>
                              <v-col cols="6" v-if="getLastProductionRating(meal.id)">
                                <div class="subtitle-2 mb-2">
                                  {{ lastReviewStale(meal.id) ? 'Earlier Review' : 'Last Production Review' }}{{
                                    getLastProductionDate(meal.id) ? ' (' + getLastProductionDate(meal.id) + ')' : ''
                                  }}
                                </div>
                                <div class="d-flex align-center">
                                  <v-chip
                                    x-small
                                    :color="getMealIdColor(getLastProductionMealId(meal.id))"
                                    text-color="white"
                                    class="mr-2"
                                  >
                                    {{ getLastProductionMealId(meal.id) }}
                                  </v-chip>
                                  <v-rating
                                    :value="getLastProductionRating(meal.id)"
                                    color="amber"
                                    dense
                                    readonly
                                    size="16"
                                    class="mr-1"
                                  />
                                  <span class="body-2">({{ getLastProductionReviewCount(meal.id) }})</span>
                                </div>
                              </v-col>
                              <v-col
                                :cols="getLastProductionRating(meal.id) ? (hasQualityIssue(meal.id) ? 4 : 6) : (hasQualityIssue(meal.id) ? 8 : 12)">
                                <div class="subtitle-2 mb-2">Overall Rating</div>
                                <div v-if="ratings[meal.id]" class="d-flex align-center flex-wrap">
                                  <div class="rating-container mb-2">
                                    <v-rating
                                      :value="Number(ratings[meal.id].overall ? ratings[meal.id].overall.average : 0)"
                                      color="amber"
                                      dense
                                      half-increments
                                      readonly
                                      size="20"
                                    />
                                    <span class="ml-2 grey--text">
                                    {{
                                        ratings[meal.id].overall ? ratings[meal.id].overall.average : 'No rating'
                                      }} ({{ ratings[meal.id].overall ? ratings[meal.id].overall.count : 0 }})
                                  </span>
                                  </div>
                                </div>
                                <div v-else class="grey--text">No rating available</div>
                              </v-col>
                              <v-col v-if="hasQualityIssue(meal.id)" cols="2"
                                     class="d-flex flex-column align-items-center justify-center">
                                <v-tooltip top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon
                                      x-large
                                      :color="lastReviewStale(meal.id) ? 'orange' : 'red'"
                                      class="throbbing-alert"
                                      v-bind="attrs"
                                      v-on="on"
                                    >
                                      mdi-alert-circle
                                    </v-icon>
                                  </template>
                                  <span>Quality Issue: Last production ({{ getLastProductionRating(meal.id) }}) significantly lower than average ({{
                                      ratings[meal.id].overall ? ratings[meal.id].overall.average : 'N/A'
                                    }})</span>
                                </v-tooltip>
                                <div v-if="lastReviewStale(meal.id)" class="caption orange--text mt-1 text-center">
                                  {{ getDaysFromLastReview(meal.id) }} days ago
                                </div>
                              </v-col>
                            </v-row>


                            <v-row v-if="getAllComments(meal.id).length > 0">
                              <v-col cols="12" class="pt-0">
                                <!-- Show overall rating for each distinct meal_id -->
                                <div class="mb-3">
                                  <div class="d-flex align-center flex-wrap">
                                    <template v-for="(mealId, index) in getDistinctMealIds(meal.id)">
                                      <v-chip
                                        v-if="index < 3 || showAllMealIds[meal.id]"
                                        :key="`rating-${mealId}`"
                                        small
                                        :color="getMealIdColor(mealId)"
                                        text-color="white"
                                        class="mr-2 mb-1"
                                      >
                                        {{ mealId }}
                                        <v-icon small class="ml-1">mdi-star</v-icon>
                                        <span class="ml-1">
                                          {{ getMealIdOverallRating(mealId) }} ({{ getMealIdReviewCount(mealId) }})
                                        </span>
                                      </v-chip>
                                    </template>
                                    <v-btn
                                      v-if="getDistinctMealIds(meal.id).length > 3"
                                      x-small
                                      outlined
                                      @click="toggleShowAllMealIds(meal.id)"
                                      class="mb-1"
                                    >
                                      {{
                                        showAllMealIds[meal.id] ? 'Show Less' : `+${getDistinctMealIds(meal.id).length - 3} More`
                                      }}
                                    </v-btn>
                                  </div>
                                </div>

                                <div v-if="feedback[meal.id] && feedback[meal.id].length"
                                     class="d-flex align-center justify-space-between mb-2">
                                  <div class="caption grey--text">Comments:</div>
                                  <v-btn
                                    x-small
                                    outlined
                                    @click="toggleReviews(meal.id)"
                                  >
                                    <v-icon small left>mdi-comment-text</v-icon>
                                    {{ showReviews[meal.id] ? 'Hide' : 'Show' }} Reviews ({{
                                      feedback[meal.id].length
                                    }})
                                  </v-btn>
                                </div>
                                <div v-else class="caption grey--text mb-1">Comments:</div>

                                <v-row>
                                  <v-col cols="12" md="6">
                                    <div class="subtitle-2 mb-2 green--text">
                                      <v-icon small color="green" class="mr-1">mdi-thumb-up</v-icon>
                                      Good (4-5 stars) - {{ getGoodReviewsCount(meal.id) }} reviews
                                    </div>
                                    <template v-if="showReviews[meal.id]">
                                      <div v-for="(comment, index) in getGoodComments(meal.id)"
                                           :key="`good-comment-${meal.id}-${index}`" class="mb-2">
                                        <div class="d-flex align-center mb-1">
                                          <v-rating
                                            :value="comment.rating"
                                            color="amber"
                                            dense
                                            readonly
                                            size="16"
                                            class="mr-2"
                                          />
                                          <span class="caption grey--text">
                                            {{ comment.rating || 'No rating' }} (1)
                                          </span>
                                        </div>
                                        <div class="body-2 font-italic">
                                          "{{ comment.text }}"
                                        </div>
                                        <div class="d-flex align-center">
                                          <span class="caption grey--text mr-2">{{ comment.date }}</span>
                                          <v-chip
                                            x-small
                                            :color="getMealIdColor(comment.mealId)"
                                            text-color="white"
                                          >
                                            {{ comment.mealId }}
                                          </v-chip>
                                        </div>
                                      </div>
                                      <div v-if="getGoodComments(meal.id).length === 0" class="caption grey--text">
                                        No good comments yet
                                      </div>
                                    </template>
                                  </v-col>

                                  <v-col cols="12" md="6">
                                    <div class="subtitle-2 mb-2 red--text">
                                      <v-icon small color="red" class="mr-1">mdi-thumb-down</v-icon>
                                      Sadness (1-3 stars) - {{ getBadReviewsCount(meal.id) }} reviews
                                    </div>
                                    <template v-if="showReviews[meal.id]">
                                      <div v-for="(comment, index) in getBadComments(meal.id)"
                                           :key="`bad-comment-${meal.id}-${index}`" class="mb-2">
                                        <div class="d-flex align-center mb-1">
                                          <v-rating
                                            :value="comment.rating"
                                            color="amber"
                                            dense
                                            readonly
                                            size="16"
                                            class="mr-2"
                                          />
                                          <span class="caption grey--text">
                                            {{ comment.rating || 'No rating' }} (1)
                                          </span>
                                        </div>
                                        <div class="body-2 font-italic">
                                          "{{ comment.text }}"
                                        </div>
                                        <div class="d-flex align-center">
                                          <span class="caption grey--text mr-2">{{ comment.date }}</span>
                                          <v-chip
                                            x-small
                                            :color="getMealIdColor(comment.mealId)"
                                            text-color="white"
                                          >
                                            {{ comment.mealId }}
                                          </v-chip>
                                        </div>
                                      </div>
                                      <div v-if="getBadComments(meal.id).length === 0" class="caption grey--text">
                                        No critical feedback yet
                                      </div>
                                    </template>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>

                            <v-row v-if="showReviews[meal.id] && feedback[meal.id] && feedback[meal.id].length">
                              <v-col cols="12">
                                <div class="subtitle-2 mb-2">All Reviews ({{ feedback[meal.id].length }})</div>
                                <div v-for="(review, index) in feedback[meal.id]" :key="`${meal.id}-review-${index}`"
                                     class="mb-3">
                                  <v-card outlined>
                                    <v-card-text class="py-2">
                                      <div class="d-flex align-center mb-2">
                                        <v-chip
                                          x-small
                                          :color="getMealIdColor(review.meal_id)"
                                          text-color="white"
                                          class="mr-2"
                                        >
                                          {{ review.meal_id }}
                                        </v-chip>
                                        <v-rating
                                          :value="review.overall || 0"
                                          color="amber"
                                          dense
                                          readonly
                                          size="16"
                                          class="mr-2"
                                        />
                                        <span class="caption grey--text mr-2">
                                        {{ review.overall || 'No rating' }}
                                      </span>
                                        <span class="caption grey--text mr-2">
                                        {{ dateFormatted(review.date, {formatString: 'MMM DD, YYYY'}) }}
                                      </span>
                                      </div>
                                      <div v-if="review.comment" class="mb-2 body-2">
                                        <strong>Comments:</strong> {{ review.comment }}
                                      </div>
                                      <v-chip-group>
                                        <v-chip
                                          v-for="property of ratingProperties.filter(p => review[p])"
                                          :key="property"
                                          x-small
                                          :color="getRatingColor(review[property])"
                                          text-color="white"
                                        >
                                          {{ property }}: {{ review[property] }}
                                        </v-chip>
                                      </v-chip-group>
                                    </v-card-text>
                                  </v-card>
                                </div>
                              </v-col>
                            </v-row>

                            <v-row v-if="!ratings[meal.id] && (!feedback[meal.id] || !feedback[meal.id].length)">
                              <v-col cols="12">
                                <v-alert outlined type="info" dense>
                                  No reviews available for this meal yet.
                                </v-alert>
                              </v-col>
                            </v-row>

                            <!-- Meal Similarity Section -->
                            <v-row
                              v-if="enableSimilarityComparisons && mealSimilarityCache[meal.id] && mealSimilarityCache[meal.id].length > 0">
                              <v-col cols="12">
                                <v-divider class="mb-3"></v-divider>
                                <div class="d-flex align-center justify-space-between mb-2">
                                  <div class="subtitle-2">
                                    <v-icon small class="mr-1">mdi-chart-scatter-plot</v-icon>
                                    Similar Meals (within 7 days)
                                    <div v-if="mealSimilarityCache[meal.id] && mealSimilarityCache[meal.id].length > 0"
                                         class="caption grey--text mt-1">
                                      Most similar: {{ mealSimilarityCache[meal.id][0].mealName }}
                                      ({{
                                        dateFormatted(mealSimilarityCache[meal.id][0].mealDate, {formatString: 'ddd MMM DD'})
                                      }})
                                      - {{ Math.round(mealSimilarityCache[meal.id][0].score * 100) }}%
                                    </div>
                                  </div>
                                  <v-btn
                                    x-small
                                    outlined
                                    @click="toggleSimilarMealDetails(meal.id)"
                                  >
                                    <v-icon small left>{{
                                        showSimilarMealDetails[meal.id] ? 'mdi-eye-off' : 'mdi-eye'
                                      }}
                                    </v-icon>
                                    {{ showSimilarMealDetails[meal.id] ? 'Hide Similar' : 'Show Similar' }}
                                  </v-btn>
                                </div>

                                <!-- Compact view (default) -->
                                <div v-if="!showSimilarMealDetails[meal.id]" class="d-flex flex-wrap">
                                  <v-chip
                                    v-for="similar in mealSimilarityCache[meal.id].slice(0, 5)"
                                    :key="`similar-${meal.id}-${similar.mealId}`"
                                    small
                                    outlined
                                    class="mr-2 mb-1"
                                    :color="similar.score > 0.7 ? 'red' : similar.score > 0.5 ? 'orange' : 'green'"
                                    :title="`${similar.mealName} (${dateFormatted(similar.mealDate, {formatString: 'MMM DD'})})`"
                                  >
                                    <v-icon small left>mdi-target</v-icon>
                                    {{ Math.round(similar.score * 100) }}% - {{ similar.daysDifference }}d
                                  </v-chip>
                                </div>
                                <div v-if="!showSimilarMealDetails[meal.id] && mealSimilarityCache[meal.id].length > 5"
                                     class="caption grey--text mt-1">
                                  ... and {{ mealSimilarityCache[meal.id].length - 5 }} more similar meals
                                </div>

                                <!-- Detailed view -->
                                <div v-if="showSimilarMealDetails[meal.id]">
                                  <!-- Current meal ingredients -->
                                  <v-card outlined class="mb-3" style="background-color: #f5f5f5;">
                                    <v-card-text class="py-2">
                                      <div class="subtitle-2 mb-2">
                                        <strong>{{ meal.name }}</strong> ingredients (for comparison):
                                      </div>
                                      <div v-if="meal.ingredients && meal.ingredients.length > 0"
                                           class="d-flex flex-wrap">
                                        <v-chip
                                          v-for="(ingredient, index) in meal.ingredients.slice(0, 15)"
                                          :key="`current-ingredient-${meal.id}-${index}`"
                                          x-small
                                          outlined
                                          class="mr-1 mb-1"
                                          :color="index < 3 ? 'blue' : 'grey'"
                                        >
                                          {{ ingredient.name }}
                                        </v-chip>
                                      </div>
                                      <div v-else class="grey--text">No ingredients listed</div>
                                      <div v-if="meal.ingredients && meal.ingredients.length > 15"
                                           class="caption grey--text mt-1">
                                        ... and {{ meal.ingredients.length - 15 }} more ingredients
                                      </div>
                                    </v-card-text>
                                  </v-card>

                                  <div v-for="similar in mealSimilarityCache[meal.id]"
                                       :key="`similar-detail-${meal.id}-${similar.mealId}`" class="mb-3">
                                    <v-card outlined>
                                      <v-card-text class="py-2">
                                        <div class="d-flex align-center mb-2">
                                          <v-chip
                                            small
                                            :color="similar.score > 0.7 ? 'red' : similar.score > 0.5 ? 'orange' : 'green'"
                                            text-color="white"
                                            class="mr-2"
                                          >
                                            {{ Math.round(similar.score * 100) }}% similarity
                                          </v-chip>
                                          <v-chip
                                            small
                                            outlined
                                            class="mr-2"
                                          >
                                            {{
                                              getRelativeTimeText(similar.mealDate, meal.date, similar.daysDifference)
                                            }}
                                          </v-chip>
                                          <router-link
                                            :to="{ name: 'MealDesign', params: { id: similar.mealId, date: formatDate(similar.mealDate)}}">
                                            <v-chip small color="primary" text-color="white">
                                              <v-icon small left>mdi-open-in-new</v-icon>
                                              View Meal
                                            </v-chip>
                                          </router-link>
                                        </div>
                                        <div class="subtitle-2 mb-2">
                                          <strong>{{ similar.mealName }}</strong>
                                          <span class="grey--text ml-2">({{
                                              dateFormatted(similar.mealDate, {formatString: 'ddd MMM DD'})
                                            }})</span>
                                        </div>
                                        <div class="body-2 mb-2">
                                          <strong>Ingredients:</strong>
                                          <span v-if="similar.ingredients.length === 0" class="grey--text ml-1">No ingredients listed</span>
                                        </div>
                                        <div v-if="similar.ingredients.length > 0" class="d-flex flex-wrap">
                                          <v-chip
                                            v-for="(ingredient, index) in similar.ingredients.slice(0, 10)"
                                            :key="`ingredient-${similar.mealId}-${index}`"
                                            x-small
                                            outlined
                                            class="mr-1 mb-1"
                                            :color="index < 3 ? 'primary' : 'grey'"
                                          >
                                            {{ ingredient.name }}
                                          </v-chip>
                                        </div>
                                        <div v-if="similar.ingredients.length > 10" class="caption grey--text mt-1">
                                          ... and {{ similar.ingredients.length - 10 }} more ingredients
                                        </div>
                                      </v-card-text>
                                    </v-card>
                                  </div>
                                </div>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-timeline-item>
            </v-timeline>

            <!-- Show Later Button -->
            <div class="text-center mt-4">
              <v-btn
                large
                color="primary"
                @click="showLater"
              >
                <v-icon left>mdi-chevron-down</v-icon>
                Show Later ({{ daysToShow }} days)
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </template>
      <v-row v-else>
        <v-col class="text-center">
          <v-progress-circular indeterminate color="primary" size="64"/>
          <div class="mt-4">Loading meals...</div>
        </v-col>
      </v-row>
    </v-container>
  </keep-alive>
</template>

<script>
import moment from 'moment';
import {mapActions, mapGetters} from 'vuex';
import {dateFormatted, formatDate,} from '@/store/utils';
import urlState from "@/router/urlState";
import api from "@/api";
import {fetchMealsInDateRange} from "@/utils/apiUtils";

// Map tod string to number
const todMapping = {
  'breakfast': 1,
  'lunch': 2,
  'dinner': 3
};


export default {
  name: "MenuScheduler",
  mixins: [urlState],
  props: {
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      todMapping: todMapping,
      mealsRaw: [],
      selectedDiet: null,
      daysToShow: 28,
      start: moment().startOf('week').format('YYYY-MM-DD'),
      loading: false,
      ratingProperties: 'overall,portion,preparation,presentation,taste'.split(','),
      feedbackBySku: {},
      ratings: {},
      feedback: {},
      showReviews: {},
      showAllMealIds: {},
      showQualityIssuesOnly: false,
      showMissingMealsOnly: false,
      showDuplicateSkusOnly: false,
      showUnscheduleConfirm: false,
      showScheduleMealDialog: false,
      showInsertDayDialog: false,
      showRemoveDayDialog: false,
      showSwapDialog: false,
      mealToUnschedule: null,
      mealToSwap: null,
      selectedSwapTargetMeal: null,
      selectedSwapTod: null,
      unscheduling: false,
      swapping: false,
      schedulingSlot: null,
      unscheduledMeals: [],
      selectedMealToSchedule: null,
      scheduling: false,
      selectedTod: null,
      maxDaysOld: 180,
      insertDayDate: null,
      insertDayDiet: null,
      insertingDay: false,
      removeDayDate: null,
      removeDayDiet: null,
      removingDay: false,
      removeDayError: null,
      selectedDuplicateSku: null,
      showSimilarMealDetails: {},
      showHighSimilarityOnly: false,
      enableSimilarityComparisons: true,
      lastFetchedParams: null,
      diets: []
    }
  },
  async mounted() {
    const dietsToShow = [1, 14, 16, 17, 18, 24];
    this.diets = Object.values(await this.fetchDiets())
      .filter(d => dietsToShow.includes(d.id));

    this.syncToUrl({
      param: 'start', urlParam: 'start', initFromRoute: true
    });
    this.syncToUrl({
      param: 'daysToShow', urlParam: 'days', initFromRoute: true
    });
    this.syncToUrl({
      param: 'selectedDiet', urlParam: 'diet', initFromRoute: true,
      parseCallback: (v) => v ? Number(v) : undefined
    });
    this.syncToUrl({
      param: 'calendarType', urlParam: 'range', initFromRoute: true
    });
    this.syncToUrl({
      param: 'enableSimilarityComparisons', urlParam: 'similarity', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    });

    if (this.selectedDiet === null && this.sortedDiets.length > 0) {
      this.selectedDiet = this.sortedDiets[0].id;
    }
  },
  computed: {
    ...mapGetters(['getDietColor', 'getDietName', 'getTimeOfDay']),
    end() {
      if (this.start && this.daysToShow) {
        return moment(this.start).add(this.daysToShow - 1, 'days').format('YYYY-MM-DD');
      } else {
        return undefined
      }
    },
    mealsSelectedByDiet() {
      return this.mealsRaw.filter(m => this.selectedDiet ? this.selectedDiet === m.diet : true);
    },
    mealsFiltered() {
      let filtered = this.mealsSelectedByDiet;

      if (this.showQualityIssuesOnly) {
        filtered = filtered.filter(m => this.hasQualityIssue(m.id));
      }

      if (this.showHighSimilarityOnly) {
        // Calculate high similarity meals directly without using cache to avoid circular dependency
        const threshold = this.calculateDynamicThreshold();
        filtered = filtered.filter(m => {
          const similarities = this.getMealSimilarityScores(m.id);
          return similarities.some(sim => sim.score >= threshold);
        });
      }

      return filtered;
    },
    qualityIssueCount() {
      return this.qualityIssueCache.length;
    },
    highSimilarityCount() {
      return this.highSimilarityMealsCache.length;
    },
    dynamicSimilarityThreshold() {
      return this.calculateDynamicThreshold();
    },
    qualityIssueCache() {
      return this.mealsRaw
        .filter(m => this.selectedDiet ? m.diet === this.selectedDiet : true)
        .filter(m => this.hasQualityIssue(m.id));
    },
    missingMealsCount() {
      // Use cached missing meals calculation
      const dates = Object.keys(this.mealsByDate);

      console.log('this.missingMealsByDateCache', this.missingMealsByDateCache[dates[0]]);
      return dates.map(date => (this.missingMealsByDateCache[date] || []).length).reduce((sum, curr) => sum + curr, 0);
    },
    duplicateSkuCount() {
      return this.duplicateSkusCache.length;
    },
    sortedDiets() {
      return this.diets; //.slice().sort((a, b) => a.id - b.id);
    },
    dietSelectItems() {
      const items = [{id: null, name: 'All'}];
      items.push(...this.sortedDiets);
      return items;
    },
    todOptions() {
      return Object.entries(todMapping)
        .map(([k, v]) => ({value: v, name: k}))
    },
    mealsByDate() {
      const grouped = {};

      // First, generate all dates in the range (excluding Sundays)
      if (this.start && this.end) {
        const currentDate = moment(this.start);
        const endDate = moment(this.end);

        while (currentDate.isSameOrBefore(endDate)) {
          // Skip Sundays (day 0) - they will only be added if they have meals
          if (currentDate.day() !== 0) {
            const dateStr = currentDate.format('YYYY-MM-DD');
            grouped[dateStr] = [];
          }
          currentDate.add(1, 'day');
        }
      }

      // Then add meals to their respective dates (including Sundays if they have meals)
      this.mealsSelectedByDiet.forEach(meal => {
        if (meal.date) {
          const date = meal.date;
          if (!grouped[date]) {
            grouped[date] = [];
          }
          grouped[date].push(meal);
        }
      });

      // Sort meals within each date
      Object.keys(grouped).forEach(date => {
        grouped[date].sort((a, b) => {
          const aTimeOrder = this.getTimeOrder(a.tod);
          const bTimeOrder = this.getTimeOrder(b.tod);
          return aTimeOrder - bTimeOrder;
        });
      });

      // Filter by quality issues if the filter is active
      if (this.showQualityIssuesOnly) {
        const filteredGrouped = {};
        Object.keys(grouped).forEach(date => {
          const mealsWithQualityIssues = grouped[date].filter(meal => this.hasQualityIssue(meal.id));
          if (mealsWithQualityIssues.length > 0) {
            filteredGrouped[date] = mealsWithQualityIssues;
          }
        });
        return filteredGrouped;
      }

      // Filter by missing meals if the filter is active
      if (this.showMissingMealsOnly) {
        const filteredGrouped = {};
        Object.keys(grouped).forEach(date => {
          if (this.calculateMissingMealsForDate(date).length > 0) {
            filteredGrouped[date] = grouped[date];
          }
        });
        return filteredGrouped;
      }

      // Filter by duplicate SKUs if the filter is active
      if (this.showDuplicateSkusOnly) {
        const filteredGrouped = {};
        Object.keys(grouped).forEach(date => {
          let mealsWithDuplicateSkus;

          if (this.selectedDuplicateSku) {
            // Filter to show only the selected duplicate SKU
            mealsWithDuplicateSkus = grouped[date].filter(meal => meal.sku === this.selectedDuplicateSku);
          } else {
            // Show all duplicate SKUs
            const duplicateSkus = this.duplicateSkusCache.map(d => d.sku);
            mealsWithDuplicateSkus = grouped[date].filter(meal =>
              duplicateSkus.includes(meal.sku)
            );
          }

          if (mealsWithDuplicateSkus.length > 0) {
            filteredGrouped[date] = mealsWithDuplicateSkus;
          }
        });
        return filteredGrouped;
      }

      // Filter by high similarity meals if the filter is active
      if (this.showHighSimilarityOnly) {
        const filteredGrouped = {};
        const threshold = this.calculateDynamicThreshold();

        Object.keys(grouped).forEach(date => {
          const mealsWithHighSimilarity = grouped[date].filter(meal => {
            const similarities = this.getMealSimilarityScores(meal.id);
            return similarities.some(sim => sim.score >= threshold);
          });

          if (mealsWithHighSimilarity.length > 0) {
            filteredGrouped[date] = mealsWithHighSimilarity;
          }
        });
        return filteredGrouped;
      }

      return grouped;
    },
    isSelectedSkuAlreadyUsed() {
      if (!this.selectedMealToSchedule || !this.selectedMealToSchedule.sku) {
        return false;
      }
      return this.mealsFiltered.some(meal => meal.sku === this.selectedMealToSchedule.sku);
    },
    // Cached computations for performance
    missingMealsByDateCache() {
      const cache = {};
      const dates = Object.keys(this.mealsByDate);
      dates.forEach(date => {
        cache[date] = this.calculateMissingMealsForDate(date);
      });
      return cache;
    },
    emptySlotsByDateCache() {
      const cache = {};
      const dates = Object.keys(this.mealsByDate);
      dates.forEach(date => {
        cache[date] = this.calculateEmptySlotsForDate(date);
      });
      return cache;
    },
    duplicateSkusCache() {
      // Cache duplicate SKUs calculation
      const mealsToCheck = this.selectedDiet === null
        ? this.mealsRaw
        : this.mealsRaw.filter(m => m.diet === this.selectedDiet);

      const skuCounts = {};
      const duplicateSkus = [];

      mealsToCheck.forEach(meal => {
        if (meal.sku) {
          skuCounts[meal.sku] = (skuCounts[meal.sku] || 0) + 1;
        }
      });

      Object.keys(skuCounts).forEach(sku => {
        if (skuCounts[sku] > 1) {
          duplicateSkus.push({
            sku: sku,
            count: skuCounts[sku],
            meals: mealsToCheck.filter(m => m.sku === sku)
          });
        }
      });

      return duplicateSkus;
    },
    // Optimized computed properties for v-for loops
    distinctMealIdsCache() {
      const cache = {};
      this.mealsRaw.forEach(meal => {
        if (this.feedback[meal.id]) {
          cache[meal.id] = [...new Set(this.feedback[meal.id].map(f => f.meal_id))];
        } else {
          cache[meal.id] = [];
        }
      });
      return cache;
    },
    goodCommentsCache() {
      const cache = {};
      this.mealsRaw.forEach(meal => {
        const allComments = this.getAllComments(meal.id);
        cache[meal.id] = allComments.filter(comment => comment.rating >= 4);
      });
      return cache;
    },
    badCommentsCache() {
      const cache = {};
      this.mealsRaw.forEach(meal => {
        const allComments = this.getAllComments(meal.id);
        cache[meal.id] = allComments.filter(comment => comment.rating <= 3);
      });
      return cache;
    },
    dateColorsCache() {
      const cache = {};
      Object.keys(this.mealsByDate).forEach(date => {
        const problems = this.getDateProblems(date);
        if (problems.includes('missing')) cache[date] = 'orange';
        else if (problems.includes('duplicate')) cache[date] = 'purple';
        else if (problems.includes('quality')) cache[date] = 'red';
        else cache[date] = 'primary';
      });
      return cache;
    },
    dateIconsCache() {
      const cache = {};
      Object.keys(this.mealsByDate).forEach(date => {
        const problems = this.getDateProblems(date);
        if (problems.includes('missing')) cache[date] = 'mdi-calendar-alert';
        else if (problems.includes('duplicate')) cache[date] = 'mdi-content-duplicate';
        else if (problems.includes('quality')) cache[date] = 'mdi-alert-circle';
        else cache[date] = 'mdi-calendar';
      });
      return cache;
    },
    timeOfDayColorsCache() {
      return {
        'breakfast': 'orange',
        'lunch': 'blue',
        'dinner': 'purple'
      };
    },
    mealIdColorsCache() {
      const colors = [
        'purple', 'indigo', 'blue', 'cyan', 'teal', 'green', 'lime',
        'yellow', 'orange', 'deep-orange', 'red', 'pink', 'brown',
        'blue-grey', 'grey'
      ];
      const cache = {};

      // Pre-calculate colors for all meal IDs that appear in feedback
      Object.values(this.feedback).flat().forEach(f => {
        if (f.meal_id && !cache[f.meal_id]) {
          const hash = f.meal_id.toString().split('').reduce((acc, char) => {
            return acc + char.charCodeAt(0);
          }, 0);
          cache[f.meal_id] = colors[hash % colors.length];
        }
      });

      return cache;
    },
    ratingColorsCache() {
      return {
        1: 'red',
        2: 'orange',
        3: 'yellow',
        4: 'light-green',
        5: 'green'
      };
    },
    mealSimilarityCache() {
      if (!this.enableSimilarityComparisons) {
        return {};
      }
      const cache = {};
      this.mealsRaw.forEach(meal => {
        if (meal.id) {
          cache[meal.id] = this.getMealSimilarityScores(meal.id);
        }
      });
      return cache;
    },
    highSimilarityMealsCache() {
      if (!this.enableSimilarityComparisons) {
        return [];
      }

      const dynamicThreshold = this.calculateDynamicThreshold();

      // If no threshold exists, return empty array
      if (dynamicThreshold === 0) {
        return [];
      }

      // Find meals that have at least one similarity score in the top 1%
      // Use mealsRaw to avoid circular dependency with mealsFiltered
      const highSimilarityMeals = [];

      this.mealsSelectedByDiet.forEach(meal => {
        if (!meal.id) return;

        const similarities = this.mealSimilarityCache[meal.id];
        if (similarities) {
          const hasHighSimilarity = similarities.some(sim => sim.score >= dynamicThreshold);
          if (hasHighSimilarity) {
            highSimilarityMeals.push(meal.id);
          }
        }
      });

      return highSimilarityMeals;
    },
    totalDisplayedMeals() {
      // Count all meals currently displayed in mealsByDate
      return Object.values(this.mealsByDate).reduce((total, dayMeals) => total + dayMeals.length, 0);
    },
    availableMealsForSwap() {
      // Return all scheduled meals from the same diet except the one being swapped
      if (!this.mealToSwap) {
        return [];
      }

      // Filter to show meals that have a date and tod, are from the same diet, and are not the current meal
      // Also filter by selected tod if specified
      return this.mealsRaw.filter(meal =>
        meal.id !== this.mealToSwap.id &&
        meal.date &&
        meal.tod &&
        meal.diet === this.mealToSwap.diet &&
        (this.selectedSwapTod === null || meal.tod === this.selectedSwapTod)
      );
    },
  },
  watch: {
    start: 'fetchData',
    startDate: 'fetchData',
    daysToShow: 'fetchData',
    selectedDiet: 'fetchData',
    mealsRaw() {
      // Set first diet as default if no diet is currently selected
      if (this.selectedDiet === null && this.sortedDiets.length > 0) {
        this.selectedDiet = this.sortedDiets[0].id;
      }
    }
  },
  methods: {
    formatDate,
    dateFormatted,
    ...mapActions(['fetchDiets']),
    isSkuAlreadyUsed(sku) {
      if (!sku) {
        return false;
      }
      return this.mealsFiltered.some(meal => meal.sku === sku);
    },
    getRatingColor(value) {
      return this.ratingColorsCache[parseInt(value)] || 'grey';
    },
    getNextInstanceId(meal) {
      // Find the largest meal ID from sku_meal_ids that is greater than the current meal ID
      if (!meal.sku_meal_ids || meal.sku_meal_ids.length === 0) {
        return null;
      }
      const maxId = Math.max(...meal.sku_meal_ids);
      return maxId > meal.id ? maxId : null;
    },
    async jumpToNextInstance(meal) {
      const nextInstanceId = this.getNextInstanceId(meal);
      if (!nextInstanceId) return;

      // Check if the meal is already loaded in mealsRaw
      const loadedMeal = this.mealsRaw.find(m => m.id === nextInstanceId);

      if (loadedMeal) {
        // Meal is already loaded, scroll to it
        console.log('meal already loaded', {nextInstanceId})
        const element = document.getElementById(`meal-${nextInstanceId}`);
        if (element) {
          console.log('found element', element);
          element.scrollIntoView({behavior: 'smooth', block: 'center'});
          return;
        } else {
          console.log('could not find page element with id', `meal-${nextInstanceId}`);
        }
      }

      // Meal is not loaded, fetch it and update the date range
      try {
        // Fetch the meal to get its date
        const response = await api.get(`v2/meal/${nextInstanceId}`);
        const nextMeal = response.data;
        console.log('loading meal')
        if (nextMeal.date) {
          // Navigate to show the date range containing the target date
          this.start = nextMeal.date;
          console.log('loaded meal, updated start', this.start);
        } else {
          console.log('loaded meal does not have date set');
        }
      } catch (error) {
        console.error('Failed to fetch next meal instance:', error);
      }
    },
    toggleReviews(mealId) {
      this.$set(this.showReviews, mealId, !this.showReviews[mealId]);
    },
    toggleShowAllMealIds(mealId) {
      this.$set(this.showAllMealIds, mealId, !this.showAllMealIds[mealId]);
    },
    toggleQualityIssueFilter() {
      this.showQualityIssuesOnly = !this.showQualityIssuesOnly;
      // Turn off other filters when quality issues filter is toggled
      if (this.showQualityIssuesOnly) {
        this.showMissingMealsOnly = false;
        this.showDuplicateSkusOnly = false;
        this.showHighSimilarityOnly = false;
      }
    },
    toggleMissingMealsFilter() {
      this.showMissingMealsOnly = !this.showMissingMealsOnly;
      // Turn off other filters when missing meals filter is toggled
      if (this.showMissingMealsOnly) {
        this.showQualityIssuesOnly = false;
        this.showDuplicateSkusOnly = false;
        this.showHighSimilarityOnly = false;
      }
    },
    toggleDuplicateSkusFilter() {
      this.showDuplicateSkusOnly = !this.showDuplicateSkusOnly;
      // Turn off other filters when duplicate SKUs filter is toggled
      if (this.showDuplicateSkusOnly) {
        this.showQualityIssuesOnly = false;
        this.showMissingMealsOnly = false;
        this.showHighSimilarityOnly = false;
      } else {
        // Reset selected duplicate SKU when filter is turned off
        this.selectedDuplicateSku = null;
      }
    },
    toggleHighSimilarityFilter() {
      this.showHighSimilarityOnly = !this.showHighSimilarityOnly;
      // Turn off other filters when high similarity filter is toggled
      if (this.showHighSimilarityOnly) {
        this.showQualityIssuesOnly = false;
        this.showMissingMealsOnly = false;
        this.showDuplicateSkusOnly = false;
        this.selectedDuplicateSku = null;
      }
    },
    showEarlier() {
      // Move start date back by the number of days to show
      this.start = moment(this.start).subtract(1, 'week').format('YYYY-MM-DD');
    },
    showLater() {
      // Move start date forward by the number of days to show
      this.start = moment(this.start).add(1, 'week').format('YYYY-MM-DD');
    },
    getAllComments(mealId) {
      if (!this.feedback[mealId] || !this.feedback[mealId].length) {
        return [];
      }

      return this.feedback[mealId]
        .filter(f => f.comment && f.comment.trim()) // Only include feedback with comments
        .map(f => ({
          text: f.comment,
          date: this.dateFormatted(f.date, {formatString: 'MMM DD, YYYY'}),
          rating: f.overall || 0,
          mealId: f.meal_id
        }));
    },
    getGoodComments(mealId) {
      return this.goodCommentsCache[mealId] || [];
    },
    getBadComments(mealId) {
      return this.badCommentsCache[mealId] || [];
    },
    getGoodReviewsCount(mealId) {
      if (!this.feedback[mealId] || !this.feedback[mealId].length) {
        return 0;
      }
      return this.feedback[mealId].filter(f => (f.overall || 0) >= 4).length;
    },
    getBadReviewsCount(mealId) {
      if (!this.feedback[mealId] || !this.feedback[mealId].length) {
        return 0;
      }
      return this.feedback[mealId].filter(f => (f.overall || 0) <= 3 && f.overall > 0).length;
    },
    getMealIdColor(mealId) {
      return this.mealIdColorsCache[mealId] || 'grey';
    },
    calculateMissingMealsForDate(date) {
      // Get all meals for this date
      const mealsForDate = this.mealsRaw.filter(m => m.date === date);
      const missingMeals = [];

      // Check each diet (exclude chef's choice diet ID 23)
      for (const diet of this.diets) {
        // Skip chef's choice diet (ID 23) or custom (ID 26)
        if (diet.id === 23 || diet.id === 26) {
          continue;
        }

        if (this.selectedDiet && diet.id !== this.selectedDiet) {
          continue;
        }

        const mealsForDiet = mealsForDate.filter(m => m.diet === diet.id);
        const timeOfDays = mealsForDiet.map(m => this.getTimeOfDay(m.tod));

        const requiredTods = ['breakfast', 'lunch', 'dinner'];
        const missing = requiredTods.filter(tod => !timeOfDays.includes(tod));

        if (missing.length > 0) {
          missingMeals.push({
            diet: diet.name,
            missing: missing
          });
        }
      }

      return missingMeals;
    },
    getMissingMealsForDate(date) {
      return this.missingMealsByDateCache[date] || [];
    },
    calculateEmptySlotsForDate(date) {
      // Get all meals for this date
      const mealsForDate = this.mealsRaw.filter(m => m.date === date);
      const emptySlots = [];

      // Check each diet (exclude chef's choice diet ID 23)
      for (const diet of this.diets) {
        // Skip chef's choice diet (ID 23) or custom (ID 26)
        if (diet.id === 23 || diet.id === 26) {
          continue;
        }
        if (this.selectedDiet && diet !== this.selectedDiet) {
          continue;
        }

        const mealsForDiet = mealsForDate.filter(m => m.diet === diet.id);
        const timeOfDays = mealsForDiet.map(m => this.getTimeOfDay(m.tod));

        const requiredTods = ['breakfast', 'lunch', 'dinner'];
        const missing = requiredTods.filter(tod => !timeOfDays.includes(tod));

        for (const tod of missing) {
          emptySlots.push({
            diet: diet.id,
            dietName: diet.name,
            tod: tod,
            date: date
          });
        }
      }

      return emptySlots;
    },
    getEmptySlotsForDate(date) {
      return this.emptySlotsByDateCache[date] || [];
    },
    getDuplicateSkus() {
      return this.duplicateSkusCache;
    },
    getDateProblems(date) {
      const problems = [];
      const dayMeals = this.mealsSelectedByDiet.filter(m => m.date === date)
        .filter(m => this.selectedDiet ? m.diet === this.selectedDiet : true);

      // Check for missing meals (only for selected diet)
      if (this.getMissingMealsForDate(date).length > 0) {
        problems.push('missing');
      }

      // Check for duplicate SKUs (within filtered meals)
      const duplicateSkus = this.getDuplicateSkus().map(d => d.sku);
      const hasDuplicateSkus = dayMeals.some(meal => duplicateSkus.includes(meal.sku));
      if (hasDuplicateSkus) {
        problems.push('duplicate');
      }

      // Check for quality issues (last, within filtered meals)
      const hasQualityIssues = dayMeals.some(meal => this.hasQualityIssue(meal.id));
      if (hasQualityIssues) {
        problems.push('quality');
      }

      return problems;
    },
    getDateColor(date) {
      return this.dateColorsCache[date] || 'primary';
    },
    getDateIcon(date) {
      return this.dateIconsCache[date] || 'mdi-calendar';
    },
    getDistinctMealIds(mealId) {
      return this.distinctMealIdsCache[mealId] || [];
    },
    getMealIdOverallRating(mealId) {
      // Find all feedback records for this specific meal_id and calculate average overall rating
      const allFeedback = Object.values(this.feedback).flat();
      const feedbackForMealId = allFeedback.filter(f => f.meal_id === mealId && f.overall);

      if (feedbackForMealId.length === 0) {
        return 0;
      }

      const sum = feedbackForMealId.reduce((total, f) => total + f.overall, 0);
      const average = sum / feedbackForMealId.length;
      return Number(average.toFixed(1));
    },
    getMealIdReviewCount(mealId) {
      // Count all feedback records for this specific meal_id
      const allFeedback = Object.values(this.feedback).flat();
      return allFeedback.filter(f => f.meal_id === mealId).length;
    },
    getLastProductionMealId(mealId) {
      // Find the highest (most recent) meal_id from feedback for this meal
      if (!this.feedback[mealId] || !this.feedback[mealId].length) {
        return null;
      }
      const mealIds = this.feedback[mealId].map(f => f.meal_id);
      return Math.max(...mealIds);
    },
    getLastProductionRating(mealId) {
      const lastProductionId = this.getLastProductionMealId(mealId);
      if (!lastProductionId) {
        return null;
      }
      return this.getMealIdOverallRating(lastProductionId);
    },
    getLastProductionReviewCount(mealId) {
      const lastProductionId = this.getLastProductionMealId(mealId);
      if (!lastProductionId) {
        return 0;
      }
      return this.getMealIdReviewCount(lastProductionId);
    },
    getLastProductionDate(mealId) {
      const lastProductionId = this.getLastProductionMealId(mealId);
      if (!lastProductionId || !this.feedback[mealId]) {
        return null;
      }

      // Find the most recent feedback record for the last production meal_id
      const lastProductionFeedback = this.feedback[mealId]
        .filter(f => f.meal_id === lastProductionId)
        .sort((a, b) => b.date.localeCompare(a.date));

      if (lastProductionFeedback.length === 0) {
        return null;
      }

      return this.dateFormatted(lastProductionFeedback[0].date, {formatString: 'MMM DD, YYYY'});
    },
    hasQualityIssue(mealId) {
      // Check if last production rating is significantly lower than overall average
      const lastProductionRating = this.getLastProductionRating(mealId);
      const overallRating = this.ratings[mealId] && this.ratings[mealId].overall ? this.ratings[mealId].overall.average : null;

      if (!lastProductionRating || !overallRating) {
        return false;
      }

      // Consider it a quality issue if:
      // 1. Last production is 2.5 or lower (poor rating)
      // 2. AND it's at least 1.0 point lower than the overall average
      return lastProductionRating <= 2.5 && (overallRating - lastProductionRating) >= 1.0;
    },
    lastReviewStale(mealId) {
      // Check if last production date is more than 130 days old
      const lastProductionId = this.getLastProductionMealId(mealId);
      if (!lastProductionId || !this.feedback[mealId]) {
        return false;
      }

      // Get the most recent feedback date for the last production
      const lastProductionFeedback = this.feedback[mealId]
        .filter(f => f.meal_id === lastProductionId)
        .sort((a, b) => b.date.localeCompare(a.date));

      if (lastProductionFeedback.length === 0) {
        return false;
      }

      // Find the meal's date from the timeline
      const meal = this.mealsRaw.find(m => m.id === mealId);
      if (!meal || !meal.date) {
        return false;
      }

      const lastProductionDate = moment(lastProductionFeedback[0].date);
      const mealDate = moment(meal.date);
      const daysDifference = mealDate.diff(lastProductionDate, 'days');

      return daysDifference > 130;
    },
    getDaysFromLastReview(mealId) {
      // Get the actual number of days between the meal and the last review
      const lastProductionId = this.getLastProductionMealId(mealId);
      if (!lastProductionId || !this.feedback[mealId]) {
        return null;
      }

      // Get the most recent feedback date for the last production
      const lastProductionFeedback = this.feedback[mealId]
        .filter(f => f.meal_id === lastProductionId)
        .sort((a, b) => b.date.localeCompare(a.date));

      if (lastProductionFeedback.length === 0) {
        return null;
      }

      // Find the meal's date from the timeline
      const meal = this.mealsRaw.find(m => m.id === mealId);
      if (!meal || !meal.date) {
        return null;
      }

      const lastProductionDate = moment(lastProductionFeedback[0].date);
      const mealDate = moment(meal.date);
      return mealDate.diff(lastProductionDate, 'days');
    },
    getTimeOfDayColor(tod) {
      return this.timeOfDayColorsCache[this.getTimeOfDay(tod)] || 'grey';
    },
    getTimeOrder(tod) {
      const order = {'breakfast': 1, 'lunch': 2, 'dinner': 3};
      return order[this.getTimeOfDay(tod)] || 99;
    },
    unscheduleMeal(meal) {
      this.mealToUnschedule = meal;
      this.showUnscheduleConfirm = true;
    },
    async confirmUnschedule() {
      if (!this.mealToUnschedule) return;

      this.unscheduling = true;
      try {
        const mealId = this.mealToUnschedule.id;
        await api.put(`v2/meal/${mealId}`, {meal: {date: null}});
        // Refresh the data to reflect the change
        await this.fetchData([mealId]);
        this.showUnscheduleConfirm = false;
        this.mealToUnschedule = null;
      } catch (error) {
        console.error('Failed to unschedule meal:', error);
        // You could add a toast notification here if available
      } finally {
        this.unscheduling = false;
      }
    },
    async openScheduleMealDialog(date, dietId, tod) {
      this.schedulingSlot = {
        date: date,
        diet: dietId,
        dietName: this.getDietName(dietId),
        tod: tod
      };


      // Set default tod based on the slot

      this.selectedTod = todMapping[tod];
      console.log('tod', tod, this.selectedTod);
      this.selectedMealToSchedule = null;
      this.showScheduleMealDialog = true;

      // Fetch meals for the default tod
      await this.fetchMealsForTod();
    },
    async fetchMealsForTod() {
      if (!this.schedulingSlot || !this.selectedTod) return;

      try {
        // Fetch SKUs with their latest meals for the selected diet and tod
        const response = await api.get('v2/meal/sku/latest', {
          params: {
            diet: this.schedulingSlot.diet,
            tod: this.selectedTod,
            maxDaysOld: this.maxDaysOld
          }
        });
        // First sort alphabetically by name
        const meals = (response.data || []).sort((a, b) => a.name.localeCompare(b.name));

        // Then sort by whether the SKU is already used (unused first, used after)
        this.unscheduledMeals = meals.sort((a, b) => {
          const aUsed = this.isSkuAlreadyUsed(a.id);
          const bUsed = this.isSkuAlreadyUsed(b.id);

          if (aUsed === bUsed) {
            // If both are used or both are unused, maintain alphabetical order
            return 0;
          }

          // Put unused SKUs first
          return aUsed ? 1 : -1;
        });
      } catch (error) {
        console.error('Failed to fetch meal SKUs:', error);
      }
    },
    async confirmScheduleMeal() {
      if (!this.selectedMealToSchedule || !this.schedulingSlot) return;

      this.scheduling = true;
      try {
        // Copy the meal from the selected SKU's latest_meal_id
        const copyResponse = await api.post(`v2/meal/copy/${this.selectedMealToSchedule.latest_meal_id}`, {copyNonSKU: true});
        const newMealId = copyResponse.data.id;

        // Set the date and time of day for the new meal (use selectedTod instead of slot tod)
        await api.put(`v2/meal/${newMealId}`, {
          meal: {
            date: this.schedulingSlot.date,
            tod: this.selectedTod
          }
        });

        // Refresh the data to reflect the change
        await this.fetchData([newMealId]);
        this.showScheduleMealDialog = false;
        this.schedulingSlot = null;
        this.selectedMealToSchedule = null;
      } catch (error) {
        console.error('Failed to schedule meal:', error);
      } finally {
        this.scheduling = false;
      }
    },
    openInsertDayDialog() {
      this.insertDayDate = null;
      this.insertDayDiet = this.selectedDiet;
      this.showInsertDayDialog = true;
    },
    async confirmInsertDay() {
      if (!this.insertDayDate || !this.insertDayDiet) return;

      this.insertingDay = true;
      try {
        await api.post('v2/meal/insert-day', {
          date: this.insertDayDate,
          diet: this.insertDayDiet
        });

        // Refresh the data to reflect the changes
        await this.fetchData();
        this.showInsertDayDialog = false;
        this.insertDayDate = null;
        this.insertDayDiet = null;
      } catch (error) {
        console.error('Failed to insert day:', error);
        // You could add a toast notification here if available
      } finally {
        this.insertingDay = false;
      }
    },
    openRemoveDayDialog() {
      this.removeDayDate = null;
      this.removeDayDiet = this.selectedDiet;
      this.removeDayError = null;
      this.showRemoveDayDialog = true;
    },
    checkRemoveDayValid() {
      this.removeDayError = null;

      if (!this.removeDayDate || !this.removeDayDiet) {
        return;
      }

      // Check if the selected date has any meals for the selected diet
      const mealsOnDate = this.mealsRaw.filter(meal =>
        meal.date === this.removeDayDate && meal.diet === this.removeDayDiet
      );

      if (mealsOnDate.length > 0) {
        this.removeDayError = `Cannot remove day: ${mealsOnDate.length} meal(s) scheduled on this date for this diet.`;
      }
    },
    async confirmRemoveDay() {
      if (!this.removeDayDate || !this.removeDayDiet || this.removeDayError) return;

      this.removingDay = true;
      try {
        await api.post('v2/meal/remove-day', {
          date: this.removeDayDate,
          diet: this.removeDayDiet
        });

        // Refresh the data to reflect the changes
        await this.fetchData();
        this.showRemoveDayDialog = false;
        this.removeDayDate = null;
        this.removeDayDiet = null;
        this.removeDayError = null;
      } catch (error) {
        console.error('Failed to remove day:', error);
        // You could add a toast notification here if available
      } finally {
        this.removingDay = false;
      }
    },
    openSwapDialog(meal) {
      this.mealToSwap = meal;
      this.selectedSwapTargetMeal = null;
      this.selectedSwapTod = meal.tod;
      this.showSwapDialog = true;
    },
    async confirmSwap() {
      if (!this.mealToSwap || !this.selectedSwapTargetMeal) return;

      this.swapping = true;
      try {
        // Store the original values
        const meal1Date = this.mealToSwap.date;
        const meal1Tod = this.mealToSwap.tod;
        const meal2Date = this.selectedSwapTargetMeal.date;
        const meal2Tod = this.selectedSwapTargetMeal.tod;

        // Swap meal 1 to meal 2's date and tod
        await api.put(`v2/meal/${this.mealToSwap.id}`, {
          meal: {
            date: meal2Date,
            tod: meal2Tod
          }
        });

        // Swap meal 2 to meal 1's date and tod
        await api.put(`v2/meal/${this.selectedSwapTargetMeal.id}`, {
          meal: {
            date: meal1Date,
            tod: meal1Tod
          }
        });

        // Refresh the data to reflect the changes
        await this.fetchData([this.mealToSwap.id, this.selectedSwapTargetMeal.id]);
        this.showSwapDialog = false;
        this.mealToSwap = null;
        this.selectedSwapTargetMeal = null;
      } catch (error) {
        console.error('Failed to swap meals:', error);
      } finally {
        this.swapping = false;
      }
    },
    async fetchData(mealIds = undefined) {
      // Only fetch if all required parameters are set
      if (!(this.start && this.daysToShow)) {
        console.log('values not set', this.start, this.daysToShow)
        return;
      }

      if (this.loading) {
        console.log('already loading')
        // return;
      }

      // Create compound key from current fetch parameters
      const currentParams = `${this.start}|${this.daysToShow}`; // |${this.selectedDiet || 'all'}`;

      // Skip fetch if parameters haven't changed
      if (this.lastFetchedParams === currentParams) {
        return;
      } else {
        this.lastFetchedParams = currentParams;
      }

      this.loading = true;


      if (mealIds && Array.isArray(mealIds) && mealIds.length > 0) {
        // Fetch specific meals by their IDs
        const response = await api.get('v2/meal/', {
          params: {
            where: {
              id: {in: mealIds}
            }
          }
        });
        const updatedMeals = response.data.meals || response.data.results || response.data.data || [];

        // Replace meals in mealsRaw with updated versions
        updatedMeals.forEach(updatedMeal => {
          const index = this.mealsRaw.findIndex(m => m.id === updatedMeal.id);
          if (index >= 0) {
            // Replace existing meal
            this.$set(this.mealsRaw, index, updatedMeal);
          } else {
            // Add new meal if it doesn't exist
            this.mealsRaw.push(updatedMeal);
          }
        });
      } else {
        // Use paginated fetch utility
        const startDate = `${this.start}T00:00:00.000Z`;
        const endDate = `${this.end}T23:59:59.999Z`;

        const options = {};
        this.mealsRaw = await fetchMealsInDateRange(startDate, endDate, options);
      }

      this.mealsRaw.sort((m1, m2) => {
        const dateCompare = (m1.date || '').localeCompare(m2.date || '');
        if (dateCompare !== 0) return dateCompare;
        return this.getTimeOrder(m1.tod) - this.getTimeOrder(m2.tod);
      });

      // Get unique SKUs from meals
      const skus = [...new Set(this.mealsRaw.map(m => m.sku).filter(sku => sku))];

      const ratings = this.ratings;

      function addRating(mealId, property, value) {
        if (value) {
          const mealRating = ratings[mealId] || (ratings[mealId] = {});
          const mealPropertyRating = mealRating[property] || (mealRating[property] = {total: 0, count: 0});
          mealPropertyRating.total += value;
          mealPropertyRating.count++;
          mealPropertyRating.average = (mealPropertyRating.total / mealPropertyRating.count).toFixed(1);
        }
      }

      // Fetch feedback by SKU instead of meal IDs
      const feedbackBySku = this.feedbackBySku;
      if (skus.length > 0) {
        const skuWithoutFeedback = skus.filter(s => !this.feedbackBySku[s]);
        if (skuWithoutFeedback.length > 0) {
          await api.post('feedback/get-by-sku', {skus: skuWithoutFeedback})
            .then(response => {
              skuWithoutFeedback.forEach(s => feedbackBySku[s] = feedbackBySku[s] || []);
              const feedbackRecords = response.data;

              // Group feedback by SKU first, then distribute to meals
              feedbackRecords.forEach(f => {
                feedbackBySku[f.sku].push(f);
              });

              // Apply feedback to all meals with the same SKU
              this.mealsRaw.forEach(meal => {
                if (meal.sku && feedbackBySku[meal.sku]) {
                  this.feedback[meal.id] = [...feedbackBySku[meal.sku]];
                  this.feedback[meal.id].sort((a, b) => b.date.localeCompare(a.date));

                  // Add ratings for this meal
                  feedbackBySku[meal.sku].forEach(f => {
                    this.ratingProperties.forEach(property => addRating(meal.id, property, f[property]));
                  });
                }
              });
            });
        }
      }

      // clear this, will stop concurrent loads, but not reloads
      // setTimeout(() => this.lastFetchedParams = null, 3000);
      // this.lastFetchedParams = null;
      this.loading = false;

    },

    // Meal similarity scoring methods
    calculateMealSimilarity(meal1, meal2) {
      if (!meal1.ingredients || !meal2.ingredients) {
        return 0;
      }

      const ingredients1 = meal1.ingredients.map(ing => ing.name.toLowerCase());
      const ingredients2 = meal2.ingredients.map(ing => ing.name.toLowerCase());

      if (ingredients1.length === 0 || ingredients2.length === 0) {
        return 0;
      }

      // Helper function to calculate weighted similarity score in one direction
      const calculateDirectionalScore = (ingredientsA, ingredientsB) => {
        let score = 0;
        let maxPossibleScore = 0;

        ingredientsA.forEach((ingredient, indexA) => {
          const weightA = 1 / (indexA + 1);
          maxPossibleScore += weightA;

          const indexB = ingredientsB.indexOf(ingredient);
          if (indexB !== -1) {
            const weightB = 1 / (indexB + 1);
            // Use minimum weight to ensure score never exceeds maxPossibleScore
            // The match quality is limited by the worse of the two positions
            const matchWeight = Math.min(weightA, weightB);
            score += matchWeight;
          }
        });

        return maxPossibleScore > 0 ? score / maxPossibleScore : 0;
      };

      // Calculate similarity in both directions and average them to ensure symmetry
      const score1to2 = calculateDirectionalScore(ingredients1, ingredients2);
      const score2to1 = calculateDirectionalScore(ingredients2, ingredients1);
      const symmetricScore = (score1to2 + score2to1) / 2;

      return Math.round(symmetricScore * 100) / 100; // Round to 2 decimal places
    },

    getMealSimilarityScores(mealId) {
      const targetMeal = this.mealsSelectedByDiet.find(m => m.id === mealId);
      if (!targetMeal || !targetMeal.date || !targetMeal.diet) {
        return [];
      }

      const targetDate = moment(targetMeal.date);
      const sevenDaysAgo = targetDate.clone().subtract(7, 'days');
      const sevenDaysFromNow = targetDate.clone().add(7, 'days');

      const similarityScores = [];

      // Find meals in the same diet within 7 days (using raw meals to avoid circular dependency)
      this.mealsSelectedByDiet.forEach(meal => {
        if (meal.id === mealId || !meal.date || meal.diet !== targetMeal.diet) {
          return;
        }

        const mealDate = moment(meal.date);
        if (mealDate.isBetween(sevenDaysAgo, sevenDaysFromNow, 'day', '[]')) {
          const score = this.calculateMealSimilarity(targetMeal, meal);
          // Only consider meals with similarity >= 10% (0.1)
          if (score >= 0.1) {
            similarityScores.push({
              mealId: meal.id,
              mealName: meal.name,
              mealDate: meal.date,
              score: score,
              daysDifference: Math.abs(targetDate.diff(mealDate, 'days')),
              ingredients: meal.ingredients || []
            });
          }
        }
      });

      // Sort by similarity score (highest first), then by days difference (closest first)
      return similarityScores.sort((a, b) => {
        if (a.score !== b.score) {
          return b.score - a.score;
        }
        return a.daysDifference - b.daysDifference;
      });
    },

    toggleSimilarMealDetails(mealId) {
      this.$set(this.showSimilarMealDetails, mealId, !this.showSimilarMealDetails[mealId]);
    },

    getRelativeTimeText(similarMealDate, currentMealDate, daysDifference) {
      if (daysDifference === 0) {
        return 'same day';
      }
      const isBefore = moment(similarMealDate).isBefore(moment(currentMealDate));
      return `${daysDifference} days ${isBefore ? 'before' : 'after'}`;
    },

    calculateDynamicThreshold() {
      if (!this.enableSimilarityComparisons) {
        return 0;
      }

      // Collect all similarity scores across all meals from raw data to avoid circular dependency
      const allScores = [];
      this.mealsSelectedByDiet.forEach(meal => {
        if (meal.id) {
          const similarities = this.getMealSimilarityScores(meal.id);
          similarities.forEach(sim => {
            allScores.push(sim.score);
          });
        }
      });

      if (allScores.length === 0) {
        return 0;
      }

      // Sort scores in descending order and get top 1% threshold
      allScores.sort((a, b) => b - a);
      const top1PercentIndex = Math.floor(allScores.length * 0.01);
      return allScores[top1PercentIndex] || 0;
    },
  }
}
</script>

<style scoped>
.v-timeline-item__body {
  padding-bottom: 24px;
}

.v-card {
  transition: all 0.3s ease;
}


.rating-container {
  display: flex;
  align-items: center;
}

.throbbing-alert {
  animation: throb 1.2s ease-in-out infinite;
}

@keyframes throb {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>